import { min } from 'lodash';
import type { IShape } from '@/utils/collision-kit';
import type { IPoint } from '@/version-types';

/**
 * 获取鼠标选择框
 *
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 */
export const getMouseMoveRect = (startPoint: IPoint, endPoint: IPoint): IShape => {
    return {
        width: Math.abs(endPoint.x - startPoint.x) || 1,
        height: Math.abs(endPoint.y - startPoint.y) || 1,
        left: min([startPoint.x, endPoint.x]) || 0,
        top: min([startPoint.y, endPoint.y]) || 0,
        rotate: 0,
    } as IShape;
};

/**
 * 获取一个点的选区
 * @param point - 坐标点
 * @returns
 */
export const getPointRect = (point: IPoint): IShape => {
    return getMouseMoveRect(point, { x: point.x + 1, y: point.y + 1 });
};

/**
 * 获取事件元素的 data-type
 *
 * @param target - 目标元素对象
 */
export function getTargetType(target: any) {
    return target?.getAttribute?.('data-type') || '';
}

/**
 * 获取操作类型
 *
 * @param target - 目标元素对象
 */
export function getOperateType(target: any) {
    return target?.getAttribute?.('data-operate-type');
}

/** 是否停止鼠标按下事件冒泡。 */
export function shouldStopMouseEvent(target: any) {
    return getTargetType(target) === STOP_MOUSE_EVENT_TYPE;
}

/** 鼠标事件传播属性。 */
export const STOP_MOUSE_EVENT_TYPE = 'stop-mouse-event-type';

/**
 * 获取操作方向
 *
 * @param target - 目标元素对象
 */
export function getOperateDirection(target: any) {
    return target?.getAttribute?.('data-direction');
}

/**
 * 获取图层 ID
 *
 * @param identity - 选择器标识 ID
 * @param target - 目标元素对象
 */
export function getLayerId(identity: string, target: any) {
    const layerId = target?.getAttribute?.('data-layer-id');
    if (!layerId) return;
    const layer = document.querySelector(`#${identity} [data-layer-id="${layerId}"]`);
    if (!layer) return;
    return layerId;
}

const getDocumentFromElement = (el: HTMLElement) => el.ownerDocument || null;

const getWindowFromElement = (el: HTMLElement) => el.ownerDocument?.defaultView || null;

export const getScrollLeftTop = (element: HTMLElement | null) => {
    const doc = element && getDocumentFromElement(element);
    let left = 0,
        top = 0;
    if (!element || !doc) {
        return { left, top };
    }
    let elementLoop: HTMLElement | Document | ShadowRoot = element;
    const docElement = doc.documentElement;
    const body = doc.body || {
        scrollLeft: 0,
        scrollTop: 0,
    };

    // While loop checks (and then sets element to) .parentNode OR .host
    //  to account for ShadowDOM. We still want to traverse up out of ShadowDOM,
    //  but the .parentNode of a root ShadowDOM node will always be null, instead
    //  it should be accessed through .host. See http://stackoverflow.com/a/24765528/4383938
    while (elementLoop && (elementLoop.parentNode || (elementLoop as unknown as ShadowRoot).host)) {
        elementLoop = (elementLoop.parentNode || (elementLoop as unknown as ShadowRoot).host) as
            | HTMLElement
            | Document
            | ShadowRoot;
        if (elementLoop === doc) {
            left = body.scrollLeft || docElement.scrollLeft || 0;
            top = body.scrollTop || docElement.scrollTop || 0;
        } else {
            left += (elementLoop as HTMLElement).scrollLeft || 0;
            top += (elementLoop as HTMLElement).scrollTop || 0;
        }

        if (elementLoop.nodeType === 1 && (elementLoop as HTMLElement).style.position === 'fixed') {
            break;
        }
    }

    return { left, top };
};

/**
 * 计算元素内容区域的偏移，允许复用边界缓存以保持坐标换算的一致性
 *
 * @param element - 目标元素
 * @param bounds - 可复用的元素边界，省略时即时测量
 * @returns 包含滚动、边框和内边距的偏移
 */
export const getElementOffset = (element: HTMLElement, bounds?: Pick<DOMRect, 'left' | 'top'>) => {
    const doc = element && getDocumentFromElement(element),
        offset = { left: 0, top: 0 };

    if (!doc) {
        return offset;
    }
    const elemStyle: CSSStyleDeclaration =
        getWindowFromElement(element)?.getComputedStyle(element, null) || ({} as CSSStyleDeclaration);
    offset.left += parseInt(elemStyle.borderLeftWidth, 10) || 0;
    offset.top += parseInt(elemStyle.borderTopWidth, 10) || 0;
    offset.left += parseInt(elemStyle.paddingLeft, 10) || 0;
    offset.top += parseInt(elemStyle.paddingTop, 10) || 0;

    let box = bounds ?? { left: 0, top: 0 };

    const docElem = doc.documentElement;
    if (!bounds && typeof element.getBoundingClientRect !== 'undefined') {
        box = element.getBoundingClientRect();
    }

    const scrollLeftTop = getScrollLeftTop(element);

    return {
        left: box.left + scrollLeftTop.left - (docElem.clientLeft || 0) + offset.left,
        top: box.top + scrollLeftTop.top - (docElem.clientTop || 0) + offset.top,
    };
};
